import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { createClient } from "@supabase/supabase-js";

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY,
);

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  // True while we're checking for an existing session on first load.
  // Keeps the app from flashing the login screen before the check completes.
  const [initialising, setInitialising] = useState(true);

  useEffect(() => {
    // Restore any existing session from localStorage on mount.
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        setToken(session.access_token);
        setUser(session.user);
      }
      setInitialising(false);
    });

    // Subscribe to auth state changes so token refreshes and sign-outs
    // (including those triggered by Supabase automatically) stay in sync.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (session) {
          setToken(session.access_token);
          setUser(session.user);
        } else {
          setToken(null);
          setUser(null);
        }
      },
    );

    return () => subscription.unsubscribe();
  }, []);

  const signIn = useCallback(async (email, password) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (authError) throw authError;
      // onAuthStateChange above will update token and user automatically,
      // but we set them here too so the UI updates without waiting for the event.
      setToken(data.session.access_token);
      setUser(data.user);
      return data;
    } catch (e) {
      setError(e.message || "Sign-in failed");
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  const signUp = useCallback(async (email, password, name) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: { data: name ? { name } : {} },
      });
      if (authError) throw authError;
      // With email confirmation on, data.session is null until the link is clicked.
      if (data.session) {
        setToken(data.session.access_token);
        setUser(data.user);
      }
      return data;
    } catch (e) {
      setError(e.message || "Sign-up failed");
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    // onAuthStateChange will clear token and user, but clear immediately too.
    setToken(null);
    setUser(null);
    setError(null);
  }, []);

  const value = useMemo(
    () => ({
      token,
      user,
      error,
      loading,
      initialising,
      signIn,
      signUp,
      signOut,
      isAuthed: Boolean(token),
    }),
    [token, user, error, loading, initialising, signIn, signUp, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
